import { Ellipsis, MessageCircleDashed, Pin, PinOff } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import zaloApi from "../../../../api/zaloApi";
import shopApi from "../../../../api/shopApi";
import { useNotification } from "../../../../hooks/useNotification";

export function MessageActionMenu({
  shop,
  msg,
  isGroup,
  isSelf,
  isPinned,
  onPinChange,
  staffName,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [isPinning, setIsPinning] = useState(false);
  const { addNotification } = useNotification();
  const menuRef = useRef(null);

  const handleUndoMessage = () => {
    zaloApi
      .undo({
        shopId: shop._id,
        id: msg.messageId,
        isGroup: isGroup,
        msgId: msg.msgId,
        cliMsgId: msg.cliMsgId,
      })
      .then((res) => {
        if (res.status !== 1) addNotification(res.message, res.status);
      });
    setIsOpen(false);
  };

  const handlePinMessage = async () => {
    if (isPinning) return;
    setIsPinning(true);

    try {
      const result = await shopApi.pinMessage({
        shopId: shop._id,
        conversationUid: msg.messageId,
        messageId: msg.id || msg._id,
        pinnedBy: shop.staffId,
        pinnedByName: staffName || "Người dùng",
      });

      if (result.status === 1) {
        addNotification("Đã ghim tin nhắn", 1);
        onPinChange?.(msg, true);
      } else {
        addNotification(result.message, result.status);
      }
    } catch (error) {
      addNotification("Có lỗi xảy ra", -1);
    } finally {
      setIsPinning(false);
      setIsOpen(false);
    }
  };

  const handleUnpinMessage = async () => {
    if (isPinning) return;
    setIsPinning(true);

    try {
      const result = await shopApi.unpinMessage({
        shopId: shop._id,
        conversationUid: msg.messageId,
        messageId: msg.id || msg._id,
        unpinnedByName: staffName || "Người dùng",
      });

      if (result.status === 1) {
        addNotification("Đã bỏ ghim tin nhắn", 1);
        onPinChange?.(msg, false);
      } else {
        addNotification(result.message, result.status);
      }
    } catch (error) {
      addNotification("Có lỗi xảy ra", -1);
    } finally {
      setIsPinning(false);
      setIsOpen(false);
    }
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        className="flex items-center justify-center h-6 w-6 rounded-full bg-white text-gray-500 shadow-sm ring-1 ring-black/5 hover:bg-gray-50 hover:text-gray-700 active:bg-gray-100 transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-blue-400/40 focus-visible:outline-none"
        onClick={() => setIsOpen(!isOpen)}
        title="Tùy chọn khác"
        aria-label="Tùy chọn khác"
        aria-expanded={isOpen}
      >
        <Ellipsis className="h-3 w-3" />
      </button>

      {isOpen && (
        <div
          className={`absolute ${
            isSelf ? "right-0" : "left-0"
          } bottom-full mb-1 bg-white rounded-xl shadow-lg ring-1 ring-black/5 py-1.5 w-52 max-h-72 overflow-y-auto z-20`}
          role="menu"
        >
          <ul className="text-sm">
            <li>
              {isPinned ? (
                <button
                  type="button"
                  className="w-full flex items-center gap-2.5 px-3 py-2 text-orange-600 hover:bg-orange-50 transition-colors disabled:opacity-60"
                  onClick={handleUnpinMessage}
                  disabled={isPinning}
                  role="menuitem"
                >
                  <PinOff className="w-4 h-4" />
                  <span>{isPinning ? "Đang xử lý..." : "Bỏ ghim"}</span>
                </button>
              ) : (
                <button
                  type="button"
                  className="w-full flex items-center gap-2.5 px-3 py-2 text-blue-600 hover:bg-blue-50 transition-colors disabled:opacity-60"
                  onClick={handlePinMessage}
                  disabled={isPinning}
                  role="menuitem"
                >
                  <Pin className="w-4 h-4" />
                  <span>{isPinning ? "Đang xử lý..." : "Ghim tin nhắn"}</span>
                </button>
              )}
            </li>

            {isSelf && (
              <li>
                <button
                  type="button"
                  className="w-full flex items-center gap-2.5 px-3 py-2 text-red-600 hover:bg-red-50 transition-colors"
                  onClick={handleUndoMessage}
                  role="menuitem"
                >
                  <MessageCircleDashed className="w-4 h-4" />
                  <span>Thu hồi</span>
                </button>
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
